import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback } from "react";
import { InvoiceWithLineItems } from "@/lib/shared-schema";
import { generateInvoicePDF, downloadInvoice } from "@/lib/pdf-generator";
import { formatCurrency, formatHours } from "@/lib/format-utils";
import { Download, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useBusinessInfo } from "@/hooks/use-business-info";
import { useAuth } from "@/hooks/use-auth";

interface InvoicePreviewModalProps {
  invoice: any | null; // Use any instead of InvoiceWithLineItems to handle both formats
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const InvoicePreviewModal = ({
  invoice,
  open,
  onOpenChange,
}: InvoicePreviewModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<{
    // Store invoice details for the preview card
    invoiceNumber: string;
    clientName: string;
    projectName: string;
    issueDate: string;
    dueDate: string;
    amount: string;
    totalHours: number;
    periodStart: string;
    periodEnd: string;
    rate?: string;
    billingContactName?: string;
    billingContactEmail?: string;
    billingAddress?: string;
    billingCity?: string;
    billingState?: string;
    billingZip?: string;
    billingCountry?: string;
  } | null>(null);

  const { toast } = useToast();
  // Fetch business info for the invoice preview
  const { data: businessInfo, isLoading: isLoadingBusinessInfo } = useBusinessInfo();
  // Get the current user information
  const { user } = useAuth();

  // Add debugging
  console.log('InvoicePreviewModal render:', {
    open,
    invoice: invoice ? {
      id: invoice.id,
      invoiceNumber: invoice.invoiceNumber,
      clientName: invoice.clientName,
      totalAmount: invoice.totalAmount,
      amount: invoice.amount
    } : null,
    previewData: previewData ? {
      invoiceNumber: previewData.invoiceNumber,
      clientName: previewData.clientName,
      amount: previewData.amount
    } : null,
    isLoading,
    isLoadingBusinessInfo,
    businessInfo: businessInfo ? {
      companyName: businessInfo.companyName
    } : null
  });

  // Function to format a date for display
  const formatDate = (date: string | Date) => {
    if (!date) return 'N/A';

    // Use the same approach as in the PDF generator
    const str = typeof date === 'string' ? date : date.toISOString();
    const datePart = str.substring(0, 10); // Get YYYY-MM-DD part
    const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${months[month - 1]} ${day}, ${year}`;
  };

  // Generate a visual preview of the invoice instead of using PDF
  // Using useCallback to memoize the function and avoid dependency cycles
  const generatePreview = useCallback(async () => {
    if (!invoice) return;

    try {
      setIsLoading(true);

      console.log('Generating preview for invoice:', invoice);

      // Handle both camelCase and snake_case field names
      const invoiceNumber = invoice.invoiceNumber || invoice.invoice_number || 'N/A';
      const clientName = invoice.clientName || invoice.client_name || 'N/A';
      const projectName = invoice.projectName || invoice.project_name || 'Consulting Services';
      const issueDate = invoice.issueDate || invoice.issue_date;
      const dueDate = invoice.dueDate || invoice.due_date;
      const totalAmount = invoice.totalAmount || invoice.amount || invoice.total_amount || 0;
      const totalHours = invoice.totalHours || invoice.total_hours || 0;
      const periodStart = invoice.periodStart || invoice.period_start;
      const periodEnd = invoice.periodEnd || invoice.period_end;
      const lineItems = invoice.lineItems || invoice.line_items || [];

      // Format dates
      const formattedPeriodStart = formatDate(periodStart);
      const formattedPeriodEnd = formatDate(periodEnd);

      // Get rate from the first line item if available
      let rate = '';
      if (lineItems && lineItems.length > 0 && lineItems[0].rate) {
        rate = formatCurrency(Number(lineItems[0].rate)) + '/hr';
      } else if (totalHours && totalAmount) {
        // Calculate effective hourly rate from total amount and hours
        const effectiveRate = Number(totalAmount) / Number(totalHours);
        if (effectiveRate > 0 && isFinite(effectiveRate)) {
          rate = formatCurrency(effectiveRate) + '/hr';
        }
      }

      // Extract the data we need for the preview
      setPreviewData({
        invoiceNumber,
        clientName,
        projectName,
        issueDate: formatDate(issueDate),
        dueDate: formatDate(dueDate),
        amount: formatCurrency(totalAmount),
        totalHours: Number(totalHours) || 0,
        periodStart: formattedPeriodStart,
        periodEnd: formattedPeriodEnd,
        rate,
        billingContactName: invoice.billingContactName || invoice.billing_contact_name || undefined,
        billingContactEmail: invoice.billingContactEmail || invoice.billing_contact_email || undefined,
        billingAddress: invoice.billingAddress || invoice.billing_address || undefined,
        billingCity: invoice.billingCity || invoice.billing_city || undefined,
        billingState: invoice.billingState || invoice.billing_state || undefined,
        billingZip: invoice.billingZip || invoice.billing_zip || undefined,
        billingCountry: invoice.billingCountry || invoice.billing_country || undefined
      });

      console.log('Preview data generated successfully:', {
        invoiceNumber,
        clientName,
        projectName,
        totalAmount,
        totalHours
      });
    } catch (error: any) {
      console.error('Error generating preview:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate invoice preview",
        variant: "destructive",
      });
      onOpenChange(false);
    } finally {
      setIsLoading(false);
    }
  }, [invoice, toast, onOpenChange]);

  // Reset state when the modal closes
  useEffect(() => {
    if (!open) {
      setPreviewData(null);
    } else if (invoice) {
      generatePreview();
    }
  }, [open, invoice, generatePreview]);

  const handleDownload = () => {
    if (!invoice) return;

    try {
      // Use the shared downloadInvoice function for consistency
      // Pass the user's name to the PDF generator
      downloadInvoice(invoice, user?.name || undefined);

      toast({
        title: "Download started",
        description: "The invoice PDF download has started.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to download the invoice",
        variant: "destructive",
      });
    }
  };

  const handleEmailInvoice = async () => {
    if (!invoice) return;

    try {
      setIsLoading(true);

      toast({
        title: 'Preparing Invoice Email',
        description: 'Downloading PDF and opening your email client...',
      });

      // Import the email service dynamically to avoid circular dependencies
      const { emailService } = await import('@/lib/email-service');

      // Use the email service to generate PDF and open email client
      await emailService.openEmailWithInvoice(invoice, undefined, user?.name);

      toast({
        title: 'Email Ready! 📧',
        description: 'Your email client has opened with the invoice details. The PDF has been downloaded to your Downloads folder - please attach it before sending.',
        duration: 8000, // Show longer so user can read the instructions
      });

      // Close the preview modal
      onOpenChange(false);
    } catch (error) {
      console.error('Error preparing invoice email:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to prepare invoice email',
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Check if this is a project-based engagement - check both camelCase and snake_case properties
  const isProjectEngagement = (invoice as any)?.engagementType === 'project' ||
    (invoice as any)?.engagement_type === 'project';
  console.log('Invoice preview - Project check:', {
    camelCase: (invoice as any)?.engagementType,
    snakeCase: (invoice as any)?.engagement_type,
    projectName: invoice?.projectName,
    isProject: isProjectEngagement
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent className="sm:max-w-4xl w-full h-full flex flex-col overflow-hidden p-0" pushed={open}>
        <SheetHeader className="p-6 border-b flex flex-row items-center justify-between">
          <SheetTitle>
            {invoice ? `Invoice #${invoice.invoiceNumber || invoice.invoice_number || 'N/A'}` : "Invoice Preview"}
          </SheetTitle>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleEmailInvoice}
              disabled={!invoice || isLoading}
            >
              Email Invoice
            </Button>
            <Button
              variant="default"
              size="sm"
              onClick={handleDownload}
              disabled={!invoice || isLoading}
            >
              <Download className="h-4 w-4 mr-2" />
              Download PDF
            </Button>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-auto bg-slate-100 p-4">
          {isLoading || isLoadingBusinessInfo ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p>Generating preview...</p>
              </div>
            </div>
          ) : !invoice ? (
            <div className="flex h-full items-center justify-center">
              <p>No invoice selected</p>
            </div>
          ) : !previewData ? (
            <div className="flex h-full flex-col items-center justify-center p-6">
              <FileText className="h-16 w-16 text-slate-400 mb-4" />
              <h3 className="text-lg font-medium mb-2">Preview not available</h3>
              <p className="text-center text-slate-600 mb-6 max-w-md">
                The invoice preview couldn't be generated. You can download the invoice as a PDF instead.
              </p>
              <Button onClick={handleDownload}>
                <Download className="h-4 w-4 mr-2" />
                Download Invoice PDF
              </Button>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto bg-white shadow-md rounded-lg overflow-hidden my-4">
              {/* Invoice Header */}
              <div className="p-6 bg-slate-50 border-b">
                <div className="flex justify-between items-start">
                  {/* Company Logo - Centered at top */}
                  <div className="w-1/3">
                    {/* Placeholder for left side */}
                  </div>

                  {/* Logo in center */}
                  <div className="flex justify-center items-center w-1/3">
                    {businessInfo?.companyLogo && (
                      <div className="max-w-[160px] max-h-[80px]">
                        <img
                          src={`/api/uploads/${businessInfo.companyLogo}`}
                          alt="Company logo"
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                    )}
                  </div>

                  {/* Invoice details at top right */}
                  <div className="text-right w-1/3">
                    <div className="text-gray-700">
                      <p className="font-semibold">Invoice #{previewData.invoiceNumber}</p>
                      <p>Date: {previewData.issueDate}</p>
                      <p>Due Date: {previewData.dueDate}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6">
                  <div className="flex flex-col items-start">
                    <h2 className="text-2xl font-bold text-gray-800">
                      {user?.name || businessInfo?.companyName || "INVOICE"}
                    </h2>
                    {/* Company Information - Reduced spacing */}
                    <div className="mt-1 text-gray-600 text-sm">
                      {businessInfo?.companyName && (
                        <p className="font-medium">{businessInfo.companyName}</p>
                      )}
                      {businessInfo?.address && (
                        <p>{businessInfo.address}</p>
                      )}
                      {/* City, State ZIP on one line if available */}
                      {(businessInfo?.city || businessInfo?.state || businessInfo?.zip) && (
                        <p>
                          {[
                            businessInfo.city,
                            businessInfo.state,
                            businessInfo.zip
                          ].filter(Boolean).join(", ")}
                        </p>
                      )}
                      {businessInfo?.phoneNumber && (
                        <p>Phone: {businessInfo.phoneNumber}</p>
                      )}
                      {businessInfo?.taxId && (
                        <p>Tax ID: {businessInfo.taxId}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Bill To section - Reduced spacing with company details */}
              <div className="p-4 border-b">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">BILL TO:</h3>
                    <p className="font-semibold text-gray-800">{previewData.clientName}</p>
                    {previewData.billingContactName && (
                      <p>ATTN: {previewData.billingContactName}</p>
                    )}
                    {previewData.billingAddress && (
                      <p>{previewData.billingAddress}</p>
                    )}
                    {/* City, State ZIP on one line if available */}
                    {(previewData.billingCity || previewData.billingState || previewData.billingZip) && (
                      <p>
                        {[
                          previewData.billingCity,
                          previewData.billingState,
                          previewData.billingZip
                        ].filter(Boolean).join(", ")}
                      </p>
                    )}
                    {previewData.billingCountry && (
                      <p>{previewData.billingCountry}</p>
                    )}
                    {previewData.billingContactEmail && (
                      <p>{previewData.billingContactEmail}</p>
                    )}
                  </div>
                  {/* Period information will be shown in the invoice summary table */}
                </div>
              </div>

              {/* Invoice Summary */}
              <div className="p-6 border-b">
                <h3 className="text-sm font-semibold uppercase text-gray-500 mb-4">Invoice Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="py-2 px-4 font-semibold text-gray-600">Description</th>
                        {!isProjectEngagement && (
                          <>
                            <th className="py-2 px-4 font-semibold text-gray-600 text-right">Hours</th>
                            <th className="py-2 px-4 font-semibold text-gray-600 text-right">Rate</th>
                          </>
                        )}
                        <th className="py-2 px-4 font-semibold text-gray-600 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-4 px-4 border-t">
                          {previewData.projectName} Consultant Activities<br />
                          <span className="text-sm text-gray-500">
                            Period: {previewData.periodStart} - {previewData.periodEnd}
                          </span>
                        </td>
                        {!isProjectEngagement && (
                          <>
                            <td className="py-4 px-4 border-t text-right">
                              {formatHours(previewData.totalHours)}
                            </td>
                            <td className="py-4 px-4 border-t text-right">
                              {previewData.rate || 'N/A'}
                            </td>
                          </>
                        )}
                        <td className="py-4 px-4 border-t text-right">
                          {previewData.amount}
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td className="py-4 px-4 border-t"></td>
                        {!isProjectEngagement && (
                          <>
                            <td className="py-4 px-4 border-t"></td>
                            <td className="py-4 px-4 border-t text-right font-semibold">Total:</td>
                          </>
                        )}
                        {isProjectEngagement && (
                          <td className="py-4 px-4 border-t text-right font-semibold">Total:</td>
                        )}
                        <td className="py-4 px-4 border-t text-right font-bold">
                          {previewData.amount}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Payment Terms */}
              <div className="p-6 text-sm text-gray-600">
                <p>Make all checks payable to the company name.</p>
                <p>Total due in 30 days.</p>
                <p className="mt-4 text-center text-gray-500">Thank you for your business!</p>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InvoicePreviewModal;